export const reviewTypeLocation = [
  {
    label: 'STANDARD REVIEW',
    options: [
      {
        label: 'Standard Review - Single Location',
        reviewType: 'Standard',
        locationType: 'Single',
      },
      {
        label: 'Standard Review - Multiple Location',
        reviewType: 'Standard',
        locationType: 'Multiple',
      },
    ],
  },
  {
    label: 'GO-LIVE REVIEW',
    options: [
      {
        label: 'Go-Live Review - Single Location',
        reviewType: 'Go-Live',
        locationType: 'Single',
      },
      {
        label: 'Go-Live Review - Multiple Location',
        reviewType: 'Go-Live',
        locationType: 'Multiple',
      },
    ],
  },
  {
    label: 'ON DEMAND REVIEW',
    options: [
      {
        label: 'On Demand Review - Single Location',
        reviewType: 'On-Demand',
        locationType: 'Single',
      },
      {
        label: 'On Demand Review - Multiple Location',
        reviewType: 'On-Demand',
        locationType: 'Multiple',
      },
    ],
  },
  {
    label: 'PROJECT-BASED REVIEW',
    options: [
      {
        label: 'Project-Based Review - Single Location',
        reviewType: 'Special Project',
        locationType: 'Single',
      },
      {
        label: 'Project-Based Review - Multiple Location',
        reviewType: 'Special Project',
        locationType: 'Multiple',
      },
    ],
  },
] as const;
/** Working days added to fieldwork / post go-live end date to compute Target Publication Date. */
export const TARGET_PUB_WORKING_DAYS_SINGLE = 30;
export const TARGET_PUB_WORKING_DAYS_MULTI  = 45;

// ── Shared activity-definition types ────────────────────────────────────────

/** Anchor date for STD / ODR / SPR activities. */
export type StdAnchor = 'start' | 'end';

/** Anchor date for Go-Live (GLRR) activities. */
export type GlrrAnchor = 'start' | 'end';

/**
 * One row of the STD/ODR/SPR timeline.
 * offset: number       — working-day offset from the anchor (negative = before, positive = after).
 * offset: null         — static row; the date is the anchor itself.
 * isUserEntered: true  — present only on static rows where the date is the value the user typed in.
 */
export interface StdActivity {
  readonly label: string;
  readonly anchor: StdAnchor;
  readonly offset: number | null;
  readonly isUserEntered?: true;
}

/**
 * One row of the Go-Live (GLRR) timeline.
 * offset: null         — static row; the date is the anchor itself.
 * isUserEntered: true  — present only on static rows where the date is the value the user typed in.
 */
export interface GlrrActivity {
  readonly label: string;
  readonly anchor: GlrrAnchor;
  readonly offset: number | null;
  readonly isUserEntered?: true;
}

// ── Shared STD / ODR / SPR rows (identical for single and multiple location) ─

const STD_COMMON_ACTIVITIES: readonly StdActivity[] = [
  { label: 'Kick-off Call',                                            anchor: 'start', offset: -50 },
  { label: 'Risk Assessment deliverables from Service Delivery',       anchor: 'start', offset: -40 },
  { label: 'Planning Call Held and Engagement Letter Issued',          anchor: 'start', offset: -15 },
  { label: 'Documentation received for review from Service Delivery',  anchor: 'start', offset:  -5 },
  { label: 'Fieldwork Start',                                          anchor: 'start', offset: null, isUserEntered: true },
  { label: 'Interim Meeting with Ops Lead',                            anchor: 'start', offset:   5 },
  { label: 'Fieldwork End',                                            anchor: 'end',   offset: null, isUserEntered: true },
  { label: 'Pre Closing meeting with Service Delivery',                anchor: 'end',   offset:   5 },
  { label: 'Draft Report send for Management Response',                anchor: 'end',   offset:  15 },
  { label: 'Closing Meeting Held',                                     anchor: 'end',   offset:  22 },
] as const;

// ── STD / ODR / SPR — Single Location ───────────────────────────────────────

/** Standard / On-Demand / Special Project timeline — Single Location (+30 WD for final rows). */
export const STD_TIMELINE_ACTIVITIES_SINGLE: readonly StdActivity[] = [
  ...STD_COMMON_ACTIVITIES,
  { label: 'Final Report Issued',             anchor: 'end', offset: TARGET_PUB_WORKING_DAYS_SINGLE },
  { label: 'Target report publication date',  anchor: 'end', offset: TARGET_PUB_WORKING_DAYS_SINGLE },
] as const;

// ── STD / ODR / SPR — Multiple Location ─────────────────────────────────────

/** Standard / On-Demand / Special Project timeline — Multiple Location (+45 WD for final rows). */
export const STD_TIMELINE_ACTIVITIES_MULTI: readonly StdActivity[] = [
  ...STD_COMMON_ACTIVITIES,
  { label: 'Final Report Issued',             anchor: 'end', offset: TARGET_PUB_WORKING_DAYS_MULTI },
  { label: 'Target report publication date',  anchor: 'end', offset: TARGET_PUB_WORKING_DAYS_MULTI },
] as const;

// ── Shared Go-Live (GLRR) rows (identical for single and multiple location) ──

/**
 * Note: POST_GO_LIVE_TESTING_END_FROM_GO_LIVE in GLRR_TIMELINE_OFFSETS (+30 WD) is a known
 * discrepancy — the spec defines Post Go-Live Testing End Date as user-entered (Anchor 2).
 * The static row below correctly uses offset: null.
 */
const GLRR_COMMON_ACTIVITIES: readonly GlrrActivity[] = [
  { label: 'Activity 01: Identify Projects for GLRR',                                                 anchor: 'start',  offset: -90  },
  { label: 'Activity 02: Information gathering / file share to mob-team',                             anchor: 'start',  offset: -75  },
  { label: 'Activity 03: Identify the projects/wave for GLRR',                                        anchor: 'start',  offset: -65  },
  { label: 'Activity 04: Kick-Off Call, EI publication, & request for process documentations',        anchor: 'start',  offset: -60  },
  { label: 'Activity 05: Pre-Go-Live Fieldwork start date',                                           anchor: 'start',  offset: -55  },
  { label: 'Activity 06: Obtain process documentation',                                               anchor: 'start',  offset: -50  },
  { label: 'Activity 07: Interim Meeting/Pre-Go-Live Fieldwork END date/GLRR INTERIM CALL DATE',  anchor: 'start',  offset: -20  },
  { label: 'Activity 08: Remediate rep-letter and observations',                                      anchor: 'start',  offset: -10  },
  { label: 'Go-Live date',                                                  anchor: 'start',  offset: null, isUserEntered: true },
  { label: 'Activity 09: Post Go-Live testing start',                                                 anchor: 'start',  offset:  10  },
  { label: 'Post Go-Live testing end date',                                 anchor: 'end', offset: null, isUserEntered: true },
  { label: 'Activity 11: Closing meeting',                                                            anchor: 'end', offset:  19  },
  { label: 'Activity 12: Project\'s action plans Received',                                           anchor: 'end', offset:  21  },
  { label: 'Activity 13: Draft report sent for manager review',                                       anchor: 'end', offset:  26  },
  { label: 'Activity 14: Draft report sent for global lead review',                                   anchor: 'end', offset:  27  },
] as const;

// ── Go-Live (GLRR) — Single Location ────────────────────────────────────────

/** Go-Live (GLRR) timeline — Single Location (+30 WD for Report Publication). */
export const GLRR_TIMELINE_ACTIVITIES_SINGLE: readonly GlrrActivity[] = [
  ...GLRR_COMMON_ACTIVITIES,
  { label: 'Report publication', anchor: 'end', offset: TARGET_PUB_WORKING_DAYS_SINGLE },
] as const;

// ── Go-Live (GLRR) — Multiple Location ──────────────────────────────────────

/** Go-Live (GLRR) timeline — Multiple Location (+45 WD for Report Publication). */
export const GLRR_TIMELINE_ACTIVITIES_MULTI: readonly GlrrActivity[] = [
  ...GLRR_COMMON_ACTIVITIES,
  { label: 'Report publication', anchor: 'end', offset: TARGET_PUB_WORKING_DAYS_MULTI },
] as const;